/**
 * Notify-me (back in stock) popup.
 *
 * Replaces Klaviyo's injected Back-in-Stock button/popup, whose content
 * couldn't be localized (Klaviyo allows only 1 BIS form per account, no
 * language variants — see snippets/product-form-container.liquid). This
 * popup is fully theme-rendered/localized and POSTs directly to Klaviyo's
 * Client Back-in-Stock Subscription API.
 *
 * Uses only the public Company ID — safe to expose in the browser.
 * Docs: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 */
document.addEventListener('DOMContentLoaded', () => {
  const modalTriggers = document.querySelectorAll('[data-modal-trigger]');
  const modalCloseButtons = document.querySelectorAll('[data-modal-close]');
  const forms = document.querySelectorAll('.js-notify-me-form');

  modalTriggers.forEach(trigger => {
    trigger.addEventListener('click', e => {
      e.preventDefault();
      const modalId = trigger.getAttribute('data-modal-trigger');
      const modal = document.getElementById(modalId);
      if (!modal) return;
      modal.classList.remove('hidden');
      modal.classList.add('flex', 'items-center', 'justify-center');
      const emailInput = modal.querySelector('input[type="email"]');
      if (emailInput) setTimeout(() => emailInput.focus(), 100);
      document.body.style.overflow = 'hidden';
    });
  });

  modalCloseButtons.forEach(closeBtn => {
    closeBtn.addEventListener('click', e => {
      e.preventDefault();
      const modalId = closeBtn.getAttribute('data-modal-close');
      const modal = document.getElementById(modalId);
      if (modal) closeModal(modal);
    });
  });

  // Close on backdrop click (the dialog wrapper itself, not the inner card).
  document.addEventListener('click', e => {
    if (
      e.target.classList.contains('fixed') &&
      e.target.hasAttribute('role') &&
      e.target.getAttribute('role') === 'dialog'
    ) {
      closeModal(e.target);
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      const openModal = document.querySelector('[role="dialog"]:not(.hidden)');
      if (openModal) closeModal(openModal);
    }
  });

  forms.forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      await handleSubmit(form);
    });
  });

  function closeModal(modal) {
    modal.classList.add('hidden');
    modal.classList.remove('flex', 'items-center', 'justify-center');
    document.body.style.overflow = '';

    const form = modal.querySelector('.js-notify-me-form');
    if (form) {
      form.reset();
      hideMessages(form);
    }
  }

  function hideMessages(form) {
    form.querySelector('.js-success-message')?.classList.add('hidden');
    form.querySelector('.js-error-message')?.classList.add('hidden');
    const emailInput = form.querySelector('input[type="email"]');
    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'false');
      emailInput.classList.remove('text-erdbeere');
    }
  }

  function showError(form) {
    const errorMsg = form.querySelector('.js-error-message');
    const successMsg = form.querySelector('.js-success-message');
    const emailInput = form.querySelector('input[type="email"]');
    if (successMsg) successMsg.classList.add('hidden');
    if (errorMsg) errorMsg.classList.remove('hidden');
    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.classList.add('text-erdbeere');
    }
  }

  function showSuccess(form) {
    const errorMsg = form.querySelector('.js-error-message');
    const successMsg = form.querySelector('.js-success-message');
    if (errorMsg) errorMsg.classList.add('hidden');
    if (successMsg) successMsg.classList.remove('hidden');
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Subscribe a variant to Klaviyo's back-in-stock notifications via the
   * public Client Back-in-Stock Subscription API. The catalog-variant id
   * must carry Klaviyo's Shopify integration prefix.
   * Docs: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
   */
  async function subscribeBackInStock(email, companyId, variantId) {
    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      }
    );

    // Success: 202 Accepted with an empty body — do NOT try to parse JSON.
    if (response.status === 202) return;

    const errorData = await response.json().catch(() => ({}));
    const detail =
      errorData.errors && errorData.errors[0] && errorData.errors[0].detail;
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }

  async function handleSubmit(form) {
    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]');
    const companyId = form.getAttribute('data-klaviyo-company-id');
    const variantId = form.getAttribute('data-variant-id');
    const email = emailInput.value.trim();

    hideMessages(form);

    // Honeypot — silently "succeed" for bots without calling Klaviyo.
    if (form.querySelector('[name="company_website"]')?.value) {
      showSuccess(form);
      return;
    }

    if (!isValidEmail(email)) {
      showError(form);
      emailInput.focus();
      return;
    }

    if (!companyId || !variantId) {
      console.error(
        '[product-notify-me] Missing Klaviyo company ID or variant ID on form',
        form
      );
      showError(form);
      return;
    }

    submitButton.disabled = true;
    submitButton.classList.add('opacity-50', 'cursor-not-allowed');

    try {
      await subscribeBackInStock(email, companyId, variantId);
      showSuccess(form);
    } catch (error) {
      console.error('[product-notify-me] Klaviyo subscription failed:', error);
      showError(form);
    } finally {
      submitButton.disabled = false;
      submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
    }
  }
});
